/* eslint-disable react/jsx-no-bind */
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import React, { FC, useCallback, useEffect, useState } from "react";

import {
  Es5LintError,
  extractEs5LintError,
  formatEs5LintError,
} from "./es5LintError";
import {
  MODULE_MODAL_CONTENT_Z_INDEX,
  MODULE_MODAL_OVERLAY_Z_INDEX,
} from "./moduleModalStacking";

export type ModuleItem = {
  id: string;
  name: string;
  description?: string | null;
  tagContent: string | null;
  draftContent?: string | null;
  hasDraft: boolean;
  hasPendingToggle?: boolean;
  hasPendingChanges?: boolean;
  effectiveEnabled?: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleItem | null;
  onSaveDraft?: (moduleId: string, content: string) => Promise<void>;
  onDiscardDraft?: (moduleId: string) => Promise<void>;
  onShowDiff?: (moduleId: string) => void;
  onSaveMetadata?: (
    moduleId: string,
    updates: {
      name: string;
      description?: string;
    }
  ) => Promise<void>;
};

export const ModuleEditorModal: FC<Props> = ({
  isOpen,
  onClose,
  module,
  onSaveDraft,
  onDiscardDraft,
  onShowDiff,
  onSaveMetadata,
}) => {
  const [draftCode, setDraftCode] = useState("");
  const [metaName, setMetaName] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedCodeChanges, setHasUnsavedCodeChanges] = useState(false);
  const [lintError, setLintError] = useState<Es5LintError | null>(null);

  const {
    isOpen: isDiscardOpen,
    onOpen: onDiscardOpen,
    onClose: onDiscardClose,
  } = useDisclosure();

  const {
    isOpen: isUnsavedCloseOpen,
    onOpen: onUnsavedCloseOpen,
    onClose: onUnsavedCloseClose,
  } = useDisclosure();

  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const unsavedCloseCancelRef = React.useRef<HTMLButtonElement>(null);

  // Sync form from server when opening a different module only — not on every Relay
  // refetch (would wipe in-progress name/description/code edits).
  useEffect(() => {
    if (!module?.id) return;
    const initialContent = module.draftContent || module.tagContent || "";
    setDraftCode(initialContent);
    setHasUnsavedCodeChanges(false);
    setLintError(null);
    setMetaName(module.name);
    setMetaDescription(module.description ?? "");
  }, [module?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- only on module id change

  // Track unsaved code changes vs server-backed editor baseline
  useEffect(() => {
    if (!module) return;

    const originalContent = module.draftContent || module.tagContent || "";
    setHasUnsavedCodeChanges(draftCode !== originalContent);
  }, [draftCode, module]);

  useEffect(() => {
    if (!isDiscardOpen && !isUnsavedCloseOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      if (isDiscardOpen) onDiscardClose();
      else onUnsavedCloseClose();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isDiscardOpen, isUnsavedCloseOpen, onDiscardClose, onUnsavedCloseClose]);

  const serverDescription = module?.description ?? "";
  const hasUnsavedMetadataChanges =
    !!module &&
    (metaName.trim() !== module.name ||
      metaDescription.trim() !== serverDescription.trim());

  const hasAnyUnsavedChanges =
    hasUnsavedCodeChanges || hasUnsavedMetadataChanges;

  const handleClose = useCallback(() => {
    if (hasAnyUnsavedChanges) {
      onUnsavedCloseOpen();
      return;
    }
    onClose();
  }, [hasAnyUnsavedChanges, onClose, onUnsavedCloseOpen]);

  const handleConfirmCloseWithUnsaved = useCallback(() => {
    onUnsavedCloseClose();
    onClose();
  }, [onClose, onUnsavedCloseClose]);

  const handleSaveDraft = useCallback(async () => {
    if (!module) return;
    const name = metaName.trim();
    if (!name) return;

    const shouldSaveMetadata =
      !!onSaveMetadata &&
      (name !== module.name ||
        metaDescription.trim() !== serverDescription.trim());
    const shouldSaveCode = !!onSaveDraft && hasUnsavedCodeChanges;

    if (!shouldSaveMetadata && !shouldSaveCode) return;

    setIsLoading(true);
    setLintError(null);
    try {
      if (shouldSaveMetadata) {
        await onSaveMetadata(module.id, {
          name,
          description: metaDescription.trim() || undefined,
        });
      }
      if (shouldSaveCode) {
        await onSaveDraft(module.id, draftCode);
        setHasUnsavedCodeChanges(false);
      }
      // Success / error toasts are shown by the parent (mutation handler).
    } catch (error) {
      const es5Error = extractEs5LintError(error);
      if (es5Error) {
        setLintError(es5Error);
      }
      // Parent already toasts and may rethrow; avoid unhandled rejection.
    } finally {
      setIsLoading(false);
    }
  }, [
    draftCode,
    hasUnsavedCodeChanges,
    metaDescription,
    metaName,
    module,
    onSaveDraft,
    onSaveMetadata,
    serverDescription,
  ]);

  const handleDiscardDraft = useCallback(async () => {
    if (!module || !onDiscardDraft) return;

    setIsLoading(true);
    try {
      await onDiscardDraft(module.id);
      setDraftCode(module.tagContent || "");
      setHasUnsavedCodeChanges(false);
      onDiscardClose();
      // Success / error toasts are shown by the parent (mutation handler).
    } catch {
      // Parent already toasts and may rethrow; avoid unhandled rejection.
    } finally {
      setIsLoading(false);
    }
  }, [module, onDiscardDraft, onDiscardClose]);

  const handleShowDiff = useCallback(() => {
    if (!module || !onShowDiff) return;
    onShowDiff(module.id);
  }, [module, onShowDiff]);

  if (!module) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="full"
      closeOnOverlayClick={
        !hasAnyUnsavedChanges && !isDiscardOpen && !isUnsavedCloseOpen
      }
      scrollBehavior="inside"
      blockScrollOnMount={false}
    >
      <ModalOverlay zIndex={MODULE_MODAL_OVERLAY_Z_INDEX} />
      <ModalContent
        containerProps={{
          zIndex: MODULE_MODAL_CONTENT_Z_INDEX,
        }}
        position="relative"
        width="90vw"
        height="90vh"
        maxWidth="90vw"
        maxHeight="90vh"
        margin="auto"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          <HStack spacing={3}>
            <Text>モジュールを編集</Text>
            {module.hasDraft && <Badge colorScheme="orange">下書きあり</Badge>}
            {hasAnyUnsavedChanges && <Badge colorScheme="red">未保存</Badge>}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody
          display="flex"
          flexDirection="column"
          gap={0}
          pb={0}
          flex={1}
          minH={0}
          overflow="hidden"
        >
          <VStack align="stretch" spacing={4} flexShrink={0} pb={2}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              モジュール情報
            </Text>
            <FormControl isRequired>
              <FormLabel fontSize="sm">モジュール名</FormLabel>
              <Input
                size="sm"
                value={metaName}
                onChange={(e) => setMetaName(e.target.value)}
                placeholder="例: ヘッダートラッキング"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">説明</FormLabel>
              <Textarea
                size="sm"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="このモジュールの説明を入力してください"
                rows={2}
                resize="vertical"
              />
            </FormControl>
            <Divider />
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              コード (対応: ES5)
            </Text>
          </VStack>

          <Box flex={1} minHeight={0}>
            <Textarea
              value={draftCode}
              onChange={(e) => {
                setDraftCode(e.target.value);
                if (lintError) setLintError(null);
              }}
              placeholder="JavaScript コードを入力..."
              fontFamily="Monaco, Consolas, 'Courier New', monospace"
              fontSize="14px"
              resize="none"
              height="100%"
              border="1px solid"
              borderColor={lintError ? "red.400" : "gray.200"}
              _focus={{
                borderColor: lintError ? "red.500" : "blue.500",
                boxShadow: lintError
                  ? "0 0 0 1px #E53E3E"
                  : "0 0 0 1px #3182ce",
              }}
            />
          </Box>

          {lintError && (
            <Text flexShrink={0} color="red.500" fontSize="sm" pt={2}>
              {formatEs5LintError(lintError)}
            </Text>
          )}

          {/* Action Buttons */}
          <HStack
            spacing={3}
            justifyContent="space-between"
            flexShrink={0}
            py={4}
          >
            <HStack spacing={3}>
              <Button
                colorScheme="blue"
                onClick={handleSaveDraft}
                isLoading={isLoading}
                isDisabled={
                  !metaName.trim() ||
                  (!hasUnsavedCodeChanges && !hasUnsavedMetadataChanges)
                }
              >
                保存
              </Button>

              <Button
                colorScheme="red"
                variant="outline"
                onClick={onDiscardOpen}
                isLoading={isLoading}
                isDisabled={!module.hasDraft}
              >
                下書きを破棄
              </Button>
            </HStack>

            <Button
              colorScheme="gray"
              variant="outline"
              onClick={handleShowDiff}
              isDisabled={!module.hasDraft}
            >
              差分プレビュー
            </Button>
          </HStack>
        </ModalBody>

        {/* In-panel confirms: nested Chakra `AlertDialog` (a second `Modal` portal) +
         * `hideOthers` / focus locks often left the editor non-interactive after cancel. */}
        {isDiscardOpen && (
          <Box
            position="absolute"
            inset={0}
            zIndex={10}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={4}
            onClick={onDiscardClose}
          >
            <Box
              bg="white"
              borderRadius="md"
              maxW="md"
              w="100%"
              p={6}
              shadow="xl"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="module-editor-discard-title"
              aria-describedby="module-editor-discard-desc"
            >
              <Text id="module-editor-discard-title" fontWeight="bold" mb={2}>
                変更を破棄
              </Text>
              <Text id="module-editor-discard-desc" fontSize="sm" mb={4}>
                コードの下書きのみ破棄します。有効/無効の予約はそのまま残ります。この操作は取り消せません。
              </Text>
              <HStack justify="flex-end" spacing={3}>
                <Button ref={cancelRef} onClick={onDiscardClose}>
                  キャンセル
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDiscardDraft}
                  isLoading={isLoading}
                >
                  破棄する
                </Button>
              </HStack>
            </Box>
          </Box>
        )}

        {isUnsavedCloseOpen && (
          <Box
            position="absolute"
            inset={0}
            zIndex={10}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={4}
            onClick={onUnsavedCloseClose}
          >
            <Box
              bg="white"
              borderRadius="md"
              maxW="md"
              w="100%"
              p={6}
              shadow="xl"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="module-editor-unsaved-title"
              aria-describedby="module-editor-unsaved-desc"
            >
              <Text id="module-editor-unsaved-title" fontWeight="bold" mb={2}>
                未保存の変更
              </Text>
              <Text id="module-editor-unsaved-desc" fontSize="sm" mb={4}>
                名前・説明またはコードに未保存の変更があります。このまま閉じると、保存していない内容は失われます。
              </Text>
              <HStack justify="flex-end" spacing={3}>
                <Button
                  ref={unsavedCloseCancelRef}
                  onClick={onUnsavedCloseClose}
                >
                  キャンセル
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleConfirmCloseWithUnsaved}
                >
                  閉じる
                </Button>
              </HStack>
            </Box>
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};
